import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useExtraditionMlaWizardContext } from '../../../hooks/useCaseRequestWizard';
import type { ExtraditionMlaWizardDraft } from '../../../types/extraditionMlaCaseRequest';
import { exceedsMax } from '../../../utils/caseFieldValidation';
import { useWizardMaxLengthChange } from '../useWizardMaxLengthChange';

import { focusFirstInvalid, type RequiredErrors } from '../../../utils/requiredValidation';
import { SectionCard } from '../WizardComponents';
import { ExtraditionMlaWizardActions } from './ExtraditionMlaWizardActions';
import { extraditionMlaWizardPath, ExtraditionMlaWizardShell, FieldError, inpClass } from './ExtraditionMlaWizardShell';
import { validateExtraditionMlaStep } from './extraditionMlaStepValidation';
import { useWizardFieldLabels } from '../useWizardFieldLabels';

const MAX_REF = 255;

export default function ExtraditionMlaStep3LegalExtraditionPage() {
  const f = useWizardFieldLabels();
  const nav = useNavigate();
  const { draft, setDraft } = useExtraditionMlaWizardContext();
  const [errors, setErrors] = useState<RequiredErrors>({});
  const showWarrantRef = draft.arrestWarrantAvailable === 'yes';

  const clearErr = (key: string) => setErrors((e) => ({ ...e, [key]: '' }));
  const maxLen = useWizardMaxLengthChange(setErrors);

  const validate = (): boolean => {
    const { valid, errors: nextErrors, specs } = validateExtraditionMlaStep(3, draft);
    setErrors(nextErrors);
    if (!valid) focusFirstInvalid(specs, nextErrors);
    return valid;
  };

  const next = () => {
    if (!validate()) return;
    nav(extraditionMlaWizardPath(4, draft.draftRequestId));
  };

  return (
    <ExtraditionMlaWizardShell currentStep={3} draft={draft}>
      <SectionCard title={f.sectionLegalExtraditionCaseDetails}>
        <div className="ccr-fg" style={{ marginBottom: 14 }}>
          <label>
            {f.arrestWarrantAvailable} <span className="req">*</span>
          </label>
          <div className="ccr-radio-row" id="emla-arrestWarrant">
            {(['yes', 'no'] as const).map((v) => (
              <label key={v} className="ccr-radio">
                <input
                  type="radio"
                  name="emla-arrestWarrant"
                  checked={draft.arrestWarrantAvailable === v}
                  onChange={() => {
                    setDraft((d: ExtraditionMlaWizardDraft) => ({
                      ...d,
                      arrestWarrantAvailable: v,
                      ...(v === 'no' ? { warrantReference: '' } : {}) }));
                    clearErr('arrestWarrantAvailable');
                    clearErr('warrantReference');
                  }}
                />
                {v === 'yes' ? f.yes : f.no}
              </label>
            ))}
          </div>
          <FieldError message={errors.arrestWarrantAvailable} />
        </div>

        {showWarrantRef ? (
          <div className="ccr-fg" style={{ marginBottom: 14 }}>
            <label>
              {f.warrantReference} <span className="req">*</span>
            </label>
            <input
              id="emla-warrantReference"
              className={inpClass(Boolean(errors.warrantReference))}
              type="text"
              value={draft.warrantReference}
              onChange={(e) => {
                setDraft((d: ExtraditionMlaWizardDraft) => ({
                  ...d,
                  warrantReference: maxLen('warrantReference', e.target.value, MAX_REF) }));
              }}
            />
            <FieldError message={errors.warrantReference} />
          </div>
        ) : null}

        <div className="ccr-fg" style={{ marginBottom: 0 }}>
          <label>{f.applicableTreatyArrangement}</label>
          <input
            id="emla-treaty"
            className={inpClass(Boolean(errors.applicableTreatyArrangement))}
            type="text"
            value={draft.applicableTreatyArrangement}
            onChange={(e) => {
              setDraft((d: ExtraditionMlaWizardDraft) => ({
                ...d,
                applicableTreatyArrangement: maxLen('applicableTreatyArrangement', e.target.value, MAX_REF) }));
            }}
          />
          <FieldError message={errors.applicableTreatyArrangement} />
        </div>
      </SectionCard>

      <ExtraditionMlaWizardActions
        draft={draft}
        setDraft={setDraft}
        currentStep={3}
        onBack={() => nav(extraditionMlaWizardPath(2, draft.draftRequestId))}
        onNext={next}
        validateBeforeSave={validate}
      />
    </ExtraditionMlaWizardShell>
  );
}
